// ============================================================
// 🏰 阿拉德战记 v5.0 — 副本引擎
// ============================================================
// 从 dnf.html 提取的所有地下城/副本相关函数
// ============================================================

import { S, save, renderTopbar, renderStatusbar } from '../state.js';
import { toast, showDropNotify, getEquipStats, rollEquipmentDrop } from '../utils.js';
import { getMonsterData, getMonsterImage, pickMonsterForRegion, pickBossForDungeon, pickEliteModifier, pickDungeonModifier, showBattleReveal } from '../data/monsters.js';
import { REGIONS, DUNGEONS, DUNGEON_CHAINS, ZONES } from '../data/world.js';
import { ROOM_TYPES, TRAVEL_EVENTS, TREASURE_OUTCOMES, ELITE_MODIFIERS, DUNGEON_MODIFIERS, MYSTERY_OUTCOMES, ROOM_GEN_CONFIG, seededRandom } from '../data/dungeon-cfg.js';
import { EQUIPMENT_DB, getEquipById, CONSUMABLES, TIERS } from '../data/equipment.js';
import { switchBGM, sfxCoin, sfxMonsterRoar } from '../audio.js';

// --- 生成地下城房间序列 ---
export function generateDungeonRooms(regionId) {
  const seed = Date.now();
  const rng = seededRandom(seed);
  const cfg = ROOM_GEN_CONFIG;
  
  const numRooms = cfg.minRooms + Math.floor(rng() * (cfg.maxRooms - cfg.minRooms + 1));
  const rooms = [];
  const nonBossCount = cfg.prepBeforeBoss ? numRooms - 2 : numRooms - 1;
  
  let hasFork = false;
  for(let i = 0; i < nonBossCount; i++) {
    const roll = rng();
    let roomType;
    if(roll < cfg.treasureChance) roomType = 'treasure';
    else if(roll < cfg.treasureChance + cfg.trapChance) roomType = 'trap';
    else if(roll < cfg.treasureChance + cfg.trapChance + cfg.restChance) roomType = 'rest';
    else if(roll < cfg.treasureChance + cfg.trapChance + cfg.restChance + cfg.mysteryChance) roomType = 'mystery';
    else if(roll < cfg.treasureChance + cfg.trapChance + cfg.restChance + cfg.mysteryChance + cfg.checkChance) roomType = 'check';
    else if(roll < cfg.treasureChance + cfg.trapChance + cfg.restChance + cfg.mysteryChance + cfg.checkChance + cfg.shrineChance) roomType = 'shrine';
    else if(!hasFork && roll < cfg.treasureChance + cfg.trapChance + cfg.restChance + cfg.mysteryChance + cfg.checkChance + cfg.shrineChance + cfg.forkChance) { roomType = 'fork'; hasFork = true; }
    else if(roll < cfg.treasureChance + cfg.trapChance + cfg.restChance + cfg.mysteryChance + cfg.checkChance + cfg.shrineChance + cfg.forkChance + cfg.eliteChance) roomType = 'elite';
    else if(roll < cfg.treasureChance + cfg.trapChance + cfg.restChance + cfg.mysteryChance + cfg.checkChance + cfg.shrineChance + cfg.forkChance + cfg.eliteChance + cfg.shopChance) roomType = 'shop';
    else roomType = 'battle';
    
    rooms.push({ type: roomType, id: 'dyn_room_'+i, cleared: false, seed: Math.floor(rng()*100000) });
  }
  
  if(cfg.prepBeforeBoss) {
    rooms.push({ type: 'prep', id: 'dyn_prep', cleared: false, seed: Math.floor(rng()*100000) });
  }
  rooms.push({ type: 'boss', id: 'dyn_boss', cleared: false, seed: Math.floor(rng()*100000) });
  
  return { rooms, seed };
}

// --- 获取房间显示信息 ---
export function getRoomDisplay(room, index, total) {
  const typeDef = ROOM_TYPES[room.type] || { icon:'⚔️', name:'战斗' };
  const cleared = room.cleared ? '✓' : '';
  const current = index === (S._dungeonRoomIndex||0) ? ' ▶' : '';
  const locked = index > (S._dungeonRoomIndex||0) ? ' 🔒' : '';
  return `${cleared}${typeDef.icon} ${typeDef.name}${current}${locked}`;
}

// --- 触发房间间随机事件 ---
export function triggerTravelEvent() {
  // 地下城修饰符影响陷阱概率
  let events = TRAVEL_EVENTS;
  if(S._dungeonModifier && S._dungeonModifier.id === 'trap_master') {
    // 陷阱大师：陷阱概率翻倍
    events = events.map(e => {
      if(e.id === 'trap_pit') return {...e, prob: e.prob * 2};
      if(e.id === 'none') return {...e, prob: e.prob * 0.7};
      return e;
    });
  }
  
  const roll = Math.random();
  let cumulative = 0;
  for(const event of events) {
    cumulative += event.prob;
    if(roll < cumulative) return event;
  }
  return events[events.length - 1]; // fallback to none
}

// --- 处理旅行事件 ---
export function processTravelEvent(event) {
  const main = document.getElementById('main');
  let html = '';
  
  switch(event.effect) {
    case 'combat': {
      const regionId = window.getCurrentRegionId() || 'forest';
      const monsterId = pickMonsterForRegion(regionId, false, false);
      S._dungeonPendingMonster = monsterId;
      S._dungeonPendingEliteMod = null;
      S._dungeonPendingRoomIdx = S._dungeonRoomIndex; // 标记为当前房间的羁绊战斗
      html = `<div class="story-box" style="border-color:var(--red);">
        <div style="color:var(--red);font-size:14px;font-weight:bold;">${event.icon} ${event.name}！</div>
        <div>${event.text}</div>
      </div>
      <button class="choice-btn" style="border-color:var(--red);" onclick="S.combat=null;startDungeonCombat('${monsterId}', ${S._dungeonRoomIndex});this.parentElement.querySelector('.story-box').remove();this.remove();">⚔️ 迎战！</button>`;
      break;
    }
    case 'gold': {
      const g = event.value.min + Math.floor(Math.random()*(event.value.max - event.value.min));
      S.gold += g;
      const modMult = S._dungeonModifier ? S._dungeonModifier.goldMult : 1;
      const actualG = Math.floor(g * modMult);
      S.gold += (actualG - g); // adjust
      html = `<div class="story-box" style="border-color:var(--gold);">
        <div style="color:var(--gold);">${event.icon} ${event.name}：${event.text}</div>
        <div style="color:var(--gold);font-size:13px;">💰 获得 <strong>${actualG}</strong> 金币！</div>
      </div>`;
      toast(`💰 +${actualG}G`, 1200);
      break;
    }
    case 'item': {
      window.addItem(event.value);
      html = `<div class="story-box" style="border-color:var(--green);">
        <div style="color:var(--green);">${event.icon} ${event.name}：${event.text}</div>
        <div style="color:var(--green);">🧪 获得药草 ×1</div>
      </div>`;
      break;
    }
    case 'hp_loss': {
      const dmg = event.value.min + Math.floor(Math.random()*(event.value.max - event.value.min));
      S.hp = Math.max(1, S.hp - dmg);
      html = `<div class="story-box" style="border-color:var(--red);">
        <div style="color:var(--red);">${event.icon} ${event.name}：${event.text}</div>
        <div style="color:var(--red);">❤️ 受到 ${dmg} 点伤害！</div>
      </div>`;
      break;
    }
    case 'spook':
      html = `<div class="story-box" style="border-color:var(--purple);">
        <div style="color:var(--purple);">${event.icon} ${event.name}</div>
        <div style="color:var(--dim);">${event.text}</div>
      </div>`;
      break;
    case 'buff': {
      html = `<div class="story-box" style="border-color:var(--blue);">
        <div style="color:var(--blue);">${event.icon} ${event.name}：${event.text}</div>
        <div style="color:var(--blue);">✨ 攻击力暂时 +${event.value.atk}！</div>
      </div>`;
      S._tempAtkBuff = (S._tempAtkBuff||0) + event.value.atk;
      S._tempAtkBuffTurns = event.value.turns;
      break;
    }
    case 'random_loot':
      const lootRoll = Math.random();
      if(lootRoll < 0.4) {
        S.gold += 100;
        html = `<div class="story-box" style="border-color:var(--gold);">🎒 背包里有100金币！</div>`;
      } else if(lootRoll < 0.7) {
        window.addItem('hp_potion_s');
        html = `<div class="story-box" style="border-color:var(--green);">🎒 背包里有一瓶药剂！</div>`;
      } else if(lootRoll < 0.9) {
        S.gold += 50; window.addItem('mp_potion_s');
        html = `<div class="story-box" style="border-color:var(--gold);">🎒 背包里有50金币和一瓶技能药剂！</div>`;
      } else {
        const eq = rollEquipmentDrop(S.lv + 2);
        html = `<div class="story-box" style="border-color:var(--purple);">🎒 背包里竟然有一件装备！</div>`;
      }
      break;
    case 'none':
    default:
      return false; // No display needed
  }
  
  if(html) {
    const existing = main.querySelector('.travel-event');
    if(existing) existing.remove();
    html = `<div class="travel-event">${html}</div>`;
    main.insertAdjacentHTML('beforeend', html);
  }
  return event.effect !== 'none';
}

// --- 应用精英怪词缀 ---
export function applyEliteModifier(monData, modifier) {
  if(!modifier) return monData;
  const m = {...monData};
  m.atk = Math.floor(m.atk * (modifier.atkMult||1));
  m.hp = Math.floor(m.hp * (modifier.hpMult||1));
  if(modifier.defBonus) m.def = (m.def||0) + modifier.defBonus;
  m._eliteModifier = modifier;
  m.name = `[${modifier.name}] ${m.name}`;
  m.gold = Math.floor(m.gold * (1 + (modifier.dropBonus||0)));
  m.exp = Math.floor(m.exp * (1 + (modifier.dropBonus||0)));
  // 精英词缀的状态攻击
  if(modifier.poison) m.statusAtk = m.statusAtk || 'poison';
  if(modifier.curse) m.statusAtk = m.statusAtk || 'curse';
  return m;
}

// --- 应用地下城全局修饰符 ---
export function applyDungeonModifierToMonster(monData) {
  if(!S._dungeonModifier) return monData;
  const dm = S._dungeonModifier;
  const m = {...monData};
  if(dm.atkMult && dm.atkMult !== 1) m.atk = Math.floor(m.atk * dm.atkMult);
  if(dm.hpMult && dm.hpMult !== 1) m.hp = Math.floor(m.hp * dm.hpMult);
  return m;
}

// --- 进入区域 ---
export function enterRegion(regionId) {
  const reg = REGIONS.find(r => r.id === regionId);
  if(!reg || S.lv < reg.lvReq) return;
  S.region = regionId;  // 记录当前区域
  S.scene = regionId;
  S.hp = Math.min(S.maxHp, S.hp + 30);
  if(S.mp) S.mp = Math.min(S.maxMp, S.mp + 10);
  save();
  window.renderRegionHub(regionId);
  renderTopbar();
  renderStatusbar();
}

// --- 返回城镇 ---
export function backToRegion() {
  const regionId = S._shopRegion || S.region || 'forest';
  S.scene = regionId;
  window.renderRegionHub(regionId);
}

// --- 进入地下城 ---
export function enterDungeon(dungeonId, difficulty) {
  const dun = findDungeonById(dungeonId);
  if(!dun) { toast('地下城不存在'); return; }

  S.currentDungeon = dun;
  S.currentDungeonDiff = difficulty;
  // 确保区域追踪（从副本反查）
  if(!S.region) {
    for(const k in DUNGEONS) {
      if(DUNGEONS[k].some(d => d.id === dungeonId)) { S.region = k; break; }
    }
  }

  // 恶魔难度加成
  if(difficulty === 'hell') {
    S._hellMode = true;
  } else {
    S._hellMode = false;
  }

  // === 关卡增强：生成随机房间序列 ===
  const regionId = window.getCurrentRegionId() || 'forest';
  const gen = generateDungeonRooms(regionId);
  S._dungeonRooms = gen.rooms;
  S._dungeonRoomIndex = 0;
  S._dungeonRoomCleared = 0;
  S._dungeonModifier = pickDungeonModifier();
  
  // 显示地下城修饰符
  if(S._dungeonModifier) {
    S._showModifierBadge = true;
  }

  // 从左到右进入第一个房间
  goToDungeonRoom(0);
  renderStatusbar();
  renderTopbar();
  save();
}

// --- 进入指定地下城房间 ---
export function goToDungeonRoom(index) {
  if(!S._dungeonRooms || index >= S._dungeonRooms.length) {
    // 地下城完成
    completeCurrentDungeon();
    return;
  }
  
  S._dungeonRoomIndex = index;
  const room = S._dungeonRooms[index];
  
  // 如果已清过的房间再次进入（回头路），不会再触发奖励
  if(room.cleared) {
    renderDungeonRoom(room, index);
    return;
  }
  
  renderDungeonRoom(room, index);
}

// --- 渲染地下城房间 ---
export function renderDungeonRoom(room, index) {
  const total = S._dungeonRooms.length;
  const regionId = window.getCurrentRegionId() || 'forest';
  const dungeonId = S.currentDungeon ? S.currentDungeon.id : null;
  const main = document.getElementById('main');
  
  // 房间进度条
  let progressHTML = `<div class="dungeon-progress" style="display:flex;gap:4px;justify-content:center;margin-bottom:10px;flex-wrap:wrap;">`;
  S._dungeonRooms.forEach((r, i) => {
    const typeDef = ROOM_TYPES[r.type] || { icon:'⚔️' };
    const isCurrent = i === index;
    const isCleared = r.cleared;
    const isLocked = i > index && !r.cleared;
    let bg = isCurrent ? 'var(--gold)' : (isCleared ? 'var(--green)' : '#333');
    let opacity = isLocked ? '0.3' : '1';
    progressHTML += `<span style="display:inline-block;width:28px;height:28px;line-height:28px;text-align:center;border-radius:50%;background:${bg};opacity:${opacity};font-size:14px;" title="${typeDef.name}">${isCleared?'✓':typeDef.icon}</span>`;
  });
  progressHTML += `</div>`;
  
  // 地下城修饰符提示
  let modifierHTML = '';
  if(S._dungeonModifier && S._showModifierBadge) {
    const dm = S._dungeonModifier;
    modifierHTML = `<div class="story-box" style="border-color:var(--purple);margin-bottom:8px;padding:8px 12px;">
      <div style="color:var(--purple);font-size:12px;font-weight:bold;">${dm.icon} 地下城异变：${dm.name}</div>
      <div style="color:var(--dim);font-size:10px;">${dm.desc}</div>
    </div>`;
    S._showModifierBadge = false;
  }
  
  const dun = S.currentDungeon;
  const diff = S.currentDungeonDiff || 'normal';
  const diffBadge = diff === 'hell' ? '💀恶魔' : '⚔️普通';
  
  switch(room.type) {
    case 'battle': {
      const monsterId = pickMonsterForRegion(regionId, false, false);
      S._dungeonPendingMonster = monsterId;
      S._dungeonPendingRoomIdx = index;
      main.innerHTML = `${modifierHTML}${progressHTML}
        <div class="story-box">
          <strong style="color:var(--gold);">${dun.name} · ${diffBadge}</strong>
          <span style="color:var(--dim);font-size:10px;"> 房间 ${index+1}/${total}</span>
          前方传来怪物的气息……准备战斗！
        </div>
        <button class="choice-btn" onclick="startDungeonCombat('${monsterId}', ${index})" style="font-size:14px;text-align:center;">⚔️ 进入战斗！</button>
        <button class="continue-btn" onclick="window.goHome()">🏠 撤退</button>`;
      break;
    }
    case 'elite': {
      const modifier = pickEliteModifier();
      const monsterId = pickMonsterForRegion(regionId, false, true);
      S._dungeonPendingMonster = monsterId;
      S._dungeonPendingEliteMod = modifier;
      S._dungeonPendingRoomIdx = index;
      main.innerHTML = `${modifierHTML}${progressHTML}
        <div class="story-box" style="border-color:var(--red);">
          <strong style="color:var(--gold);">${dun.name} · ${diffBadge}</strong>
          <span style="color:var(--dim);font-size:10px;"> 房间 ${index+1}/${total}</span>
          <span style="color:var(--red);">👹 精英怪出现了！</span>
          <span style="color:var(--orange);">【${modifier.name}】${modifier.text}</span>
        </div>
        <button class="choice-btn" style="border-color:var(--red);" onclick="startDungeonCombat('${monsterId}', ${index})" style="font-size:14px;text-align:center;">👹 迎战精英怪！</button>
        <button class="continue-btn" onclick="window.goHome()">🏠 撤退</button>`;
      break;
    }
    case 'treasure': {
      room.cleared = true;
      S._dungeonRoomCleared++;
      const outcome = TREASURE_OUTCOMES[Math.floor(Math.random() * TREASURE_OUTCOMES.length)];
      const modMult = S._dungeonModifier ? S._dungeonModifier.goldMult : 1;
      const baseGold = 30 + Math.floor(Math.random() * 70);
      const gold = Math.floor(baseGold * (outcome.goldMult || 1) * modMult);
      
      S.gold += gold;
      save();
      
      if(outcome.mimic) {
        const mId = pickMonsterForRegion(regionId, false, true);
        main.innerHTML = `${modifierHTML}${progressHTML}
          <div class="story-box" style="border-color:var(--red);">
            ${outcome.icon} <strong style="color:var(--red);">${outcome.name}！</strong>
            ${outcome.text}
          </div>
          <button class="choice-btn" style="border-color:var(--red);" onclick="startDungeonCombat('${mId}', ${index})">👹 迎战宝箱怪！</button>`;
        S._dungeonPendingMonster = mId;
        S._dungeonPendingRoomIdx = index;
        room.cleared = false; // 还需要战斗
        return;
      }
      
      let itemHTML = '';
      if(outcome.itemChance > 0 && Math.random() < outcome.itemChance) {
        const tierBonus = outcome.tierBonus || 0;
        const dropItem = rollEquipmentDrop(S.lv + tierBonus);
        if(dropItem) {
          itemHTML = `<div style="color:${TIERS[dropItem.tier].color};">🎁 获得装备：${dropItem.name}！</div>`;
        }
      }
      
      if(outcome.curse) {
        S.hp = Math.max(1, Math.floor(S.hp * 0.7));
        main.innerHTML = `${modifierHTML}${progressHTML}
          <div class="story-box" style="border-color:var(--purple);">
            ${outcome.icon} <strong style="color:var(--purple);">${outcome.name}！</strong>
            ${outcome.text}
            <div style="color:var(--red);">☠️ 黑暗能量侵蚀……HP减少30%</div>
            <div style="color:var(--gold);">💰 获得 ${gold} 金币</div>
            ${itemHTML}
          </div>
          <button class="continue-btn" onclick="advanceDungeonRoom(${index})">继续探索 →</button>`;
      } else {
        main.innerHTML = `${modifierHTML}${progressHTML}
          <div class="story-box" style="border-color:var(--gold);">
            ${outcome.icon} <strong style="color:var(--gold);">${outcome.name}！</strong>
            ${outcome.text}
            <div style="color:var(--gold);">💰 获得 ${gold} 金币</div>
            ${itemHTML}
          </div>
          <button class="continue-btn" onclick="advanceDungeonRoom(${index})">继续探索 →</button>`;
      }
      break;
    }
    case 'trap': {
      room.cleared = true;
      S._dungeonRoomCleared++;
      const trapDmg = 10 + Math.floor(Math.random() * 25);
      const trapGold = 20 + Math.floor(Math.random() * 60);
      S.hp = Math.max(1, S.hp - trapDmg);
      S.gold += trapGold;
      S._trapRoomIndex = index;
      save();
      main.innerHTML = `${modifierHTML}${progressHTML}
        <div class="story-box" style="border-color:var(--orange);">
          ⚠️ <strong style="color:var(--orange);">陷阱触发！</strong>
          你踩到了陷阱机关！墙壁上射出毒箭……
          <div style="color:var(--red);">❤️ HP -${trapDmg}</div>
          <div style="color:var(--gold);">💰 在陷阱旁找到了 ${trapGold} 金币</div>
        </div>
        <div class="choices">
          <button class="choice-btn" style="border-color:var(--blue);font-size:12px;" onclick="window.tryDodgeTrap(${index},${trapDmg},${trapGold})">💨 尝试闪避！掷骰 ≥ 4 可避免伤害（+额外金币）</button>
        </div>
        <button class="continue-btn" onclick="advanceDungeonRoom(${index})">小心前行 →</button>`;
      break;
    }
    case 'check': {
      const challenge = window.SKILL_CHECKS[Math.floor(Math.random() * window.SKILL_CHECKS.length)];
      const statKey = challenge.stat;
      const statNames = { atk:'攻击力', int:'智力', spd:'速度' };
      const playerVal = statKey === 'atk' ? (S.atk||0) : (statKey === 'int' ? (getEquipStats().bonusInt||0)+5 : (S.spd||0));
      S._checkRoomIndex = index;
      main.innerHTML = `${modifierHTML}${progressHTML}
        <div class="story-box" style="border-color:var(--blue);">
          <div style="font-size:15px;color:var(--blue);font-weight:bold;">${challenge.icon} 考验：${challenge.name}</div>
          <div style="margin:8px 0;">${challenge.text}</div>
          <div style="font-size:11px;color:var(--dim);">所需：${statNames[statKey]} ≥ ${challenge.threshold}（你：${playerVal}）</div>
        </div>
        <div class="choices">
          <button class="choice-btn skill-btn" onclick="window.attemptSkillCheck(${index}, '${challenge.id}', true)">${challenge.icon} 尝试通过</button>
          <button class="continue-btn" style="font-size:11px;" onclick="window.skipSkillCheck(${index})">⏭️ 放弃，绕路前行</button>
        </div>`;
      break;
    }
    case 'shrine': {
      S._shrineRoomIndex = index;
      main.innerHTML = `${modifierHTML}${progressHTML}
        <div class="story-box" style="border-color:var(--gold);">
          <div style="font-size:15px;color:var(--gold);font-weight:bold;">⛩️ 古老祭坛</div>
          <div style="margin:8px 0;">一座布满灰尘的祭坛。石碑上刻着：「付出代价，得获力量。」</div>
        </div>
        <div class="choices">
          <button class="choice-btn" style="border-color:var(--red);" onclick="window.shrineSacrificeHP(${index})">🔴 献祭生命（-40%HP）→ 攻击力永久+5</button>
          <button class="choice-btn" style="border-color:var(--gold);" onclick="window.shrineSacrificeGold(${index})">🟡 献祭金币（-300G）→ 随机装备</button>
          <button class="choice-btn" style="border-color:var(--purple);font-size:13px;" onclick="window.shrineFateDice(${index})">🎲 命运之骰 —— 掷骰！≥4得祝福，<4遭反噬</button>
          <button class="continue-btn" style="font-size:11px;" onclick="window.shrineSkip(${index})">⚠️ 离开祭坛</button>
        </div>`;
      break;
    }
    case 'fork': {
      window.renderForkRoom(index);
      return;
    }
    case 'prep': {
      room.cleared = true; S._dungeonRoomCleared++; save();
      const dun = S.currentDungeon || {};
      main.innerHTML = `${modifierHTML}${progressHTML}
        <div class="story-box" style="border-color:var(--red);text-align:center;">
          <div style="font-size:18px;color:var(--red);font-weight:900;">⚠️ 前方高能警告</div>
          <div style="margin:6px 0;color:var(--dim);">魔力波动越来越强烈……${dun.name||'地下城'}的守关者就在前方。</div>
        </div>
        <div class="choices">
          <button class="choice-btn" style="border-color:var(--gold);" onclick="window.prepBless(${index})">🛡️ 战前祝福 — 200G换攻击+5/防御+3</button>
          <button class="choice-btn" style="border-color:var(--purple);" onclick="window.prepSharpen(${index})">🔪 磨刀 — 本次战斗攻击+15%</button>
          <button class="choice-btn" style="border-color:var(--green);" onclick="window.prepRest(${index})">🔥 修整 — 恢复30%HP/MP</button>
          <button class="choice-btn" style="border-color:var(--red);font-size:14px;" onclick="window.enterBossFromPrep(${index})">💀 直接挑战！（掉落+30%）</button>
        </div>`;
      break;
    }
    case 'rest': {
      room.cleared = true;
      S._dungeonRoomCleared++;
      const healHP = Math.floor(S.maxHp * 0.3);
      const healMP = Math.floor((S.maxMp||50) * 0.4);
      S.hp = Math.min(S.maxHp, S.hp + healHP);
      S.mp = Math.min(S.maxMp||50, (S.mp||0) + healMP);
      S._restRoomIndex = index;
      save();
      main.innerHTML = `${modifierHTML}${progressHTML}
        <div class="story-box" style="border-color:var(--green);">
          🔥 <strong style="color:var(--green);">篝火营地</strong>
          一处安全的地点。温暖的篝火驱散了地下城的寒意……你注意到篝火旁躺着一枚发光的硬币。
          <div style="color:var(--green);">💚 HP恢复了 ${healHP} 点</div>
          <div style="color:var(--blue);">💠 MP恢复了 ${healMP} 点</div>
        </div>
        <div class="choices">
          <button class="choice-btn" style="border-color:var(--gold);font-size:13px;" onclick="window.rollFateCoin(${index})">🪙 掷命运硬币 —— 正面：HP/MP全恢复 | 反面：HP减半</button>
        </div>
        <button class="continue-btn" onclick="advanceDungeonRoom(${index})">继续探索 →</button>
        <button class="continue-btn" style="margin-top:4px;background:rgba(201,164,75,0.08);" onclick="window.goHome()">🏠 返回大厅（保存进度）</button>`;
      break;
    }
    case 'mystery': {
      room.cleared = true;
      S._dungeonRoomCleared++;
      save();
      // 三扇门，其中一扇门后是陷阱
      const doorIdx = Math.floor(Math.random() * 3);
      S._mysteryRoomIndex = index;
      S._mysteryBad = doorIdx;
      const doors = ['🔮 左门：门缝透出金色光芒', '🌑 中门：门后传来低沉呼吸声', '⚡ 右门：门把手上缠绕着电弧'];
      main.innerHTML = `${modifierHTML}${progressHTML}
        <div class="story-box" style="border-color:var(--purple);text-align:center;">
          <div style="font-size:16px;color:var(--purple);font-weight:bold;">🚪 三扇门</div>
          <div style="margin:8px 0;color:var(--dim);">前方有三扇门。直觉告诉你——其中一扇后面有危险，另外两扇藏着宝物。</div>
          <div style="font-size:11px;color:#666;">选一扇推开……赌一把你的运气。</div>
        </div>
      `;
      for(let d=0; d<3; d++) {
        main.innerHTML += `<button class="choice-btn" style="margin:4px auto;max-width:320px;font-size:13px;" onclick="window.pickMysteryDoor(${index},${d})">${doors[d]}</button>`;
      }
      main.innerHTML += `<button class="continue-btn" style="font-size:11px;margin-top:8px;" onclick="window.skipMysteryDoor(${index})">⏭️ 三扇都不进，绕路</button>`;
      break;
    }
    // 保留旧逻辑的fallback (由pickMysteryDoor调用)
    case '__mystery_resolve__': { const outcome = { icon:'',name:'',text:'' };
      switch(outcome.effect) {
        case 'full_heal':
          S.hp = S.maxHp; S.mp = S.maxMp||50;
          main.innerHTML = `${modifierHTML}${progressHTML}<div class="story-box" style="border-color:var(--blue);">${outcome.icon} <strong style="color:var(--blue);">${outcome.name}！</strong>${outcome.text}<div style="color:var(--green);">💚 HP和MP完全恢复！</div></div><button class="continue-btn" onclick="advanceDungeonRoom(${index})">继续 →</button>`;
          break;
        case 'combat':
          const mId = pickMonsterForRegion(regionId, false, Math.random()<0.3);
          main.innerHTML = `${modifierHTML}${progressHTML}<div class="story-box" style="border-color:var(--red);">${outcome.icon} <strong style="color:var(--red);">${outcome.name}！</strong>${outcome.text}</div><button class="choice-btn" style="border-color:var(--red);" onclick="startDungeonCombat('${mId}', ${index})">⚔️ 迎战！</button>`;
          S._dungeonPendingMonster = mId;
          S._dungeonPendingRoomIdx = index;
          room.cleared = false;
          return;
        case 'big_loot':
          const bg = 100 + Math.floor(Math.random()*200);
          S.gold += bg;
          main.innerHTML = `${modifierHTML}${progressHTML}<div class="story-box" style="border-color:var(--gold);">${outcome.icon} <strong style="color:var(--gold);">${outcome.name}！</strong>${outcome.text}<div style="color:var(--gold);">💰 获得 ${bg} 金币！</div></div><button class="continue-btn" onclick="advanceDungeonRoom(${index})">继续 →</button>`;
          break;
        case 'perm_buff':
          S.atk += outcome.value.atk; S.def += outcome.value.def;
          main.innerHTML = `${modifierHTML}${progressHTML}<div class="story-box" style="border-color:var(--blue);">${outcome.icon} <strong style="color:var(--blue);">${outcome.name}！</strong>${outcome.text}<div style="color:var(--blue);">✨ 攻击力永久 +${outcome.value.atk}，防御力 +${outcome.value.def}！</div></div><button class="continue-btn" onclick="advanceDungeonRoom(${index})">继续 →</button>`;
          break;
        case 'skip_room':
          main.innerHTML = `${modifierHTML}${progressHTML}<div class="story-box" style="border-color:var(--purple);">${outcome.icon} <strong style="color:var(--purple);">${outcome.name}！</strong>${outcome.text}</div><button class="continue-btn" onclick="advanceDungeonRoom(${index}, true)">继续 →</button>`;
          room.cleared = true;
          return;
        default:
          main.innerHTML = `${modifierHTML}${progressHTML}<div class="story-box" style="border-color:var(--dim);">${outcome.icon} <strong>${outcome.name}</strong>${outcome.text}</div><button class="continue-btn" onclick="advanceDungeonRoom(${index})">继续 →</button>`;
      }
      break;
    }
    case 'shop': {
      room.cleared = true;
      S._dungeonRoomCleared++;
      spawnDungeonMerchant();
      return;
    }
    case 'boss': {
      if(room.cleared) {
        // Boss已被击败，显示已清状态
        main.innerHTML = `${modifierHTML}${progressHTML}
          <div class="story-box" style="border-color:var(--gold);text-align:center;">
            <div style="font-size:40px;">💀</div>
            <div style="color:var(--gold);font-size:16px;font-weight:bold;">BOSS已被击败</div>
            <div style="color:var(--dim);margin-top:4px;">${dun.name}的守卫已经倒下……前方或许还有路。</div>
          </div>
          <button class="continue-btn" onclick="advanceDungeonRoom(${index})">继续探索 →</button>`;
        break;
      }
      const monsterId = pickBossForDungeon(dungeonId, regionId);
      S._dungeonPendingMonster = monsterId;
      S._dungeonPendingRoomIdx = index;
      main.innerHTML = `${modifierHTML}${progressHTML}
        <div class="boss-room-intro">
          <div class="boss-room-skull">💀</div>
          <div class="boss-room-title">${getMonsterData(monsterId).name}</div>
          <div class="boss-room-sub">${dun.name} · ${diffBadge} · 房间 ${index+1}/${total}</div>
          <div class="boss-room-desc">强大的气息扑面而来，这是地下城最深处的守卫……</div>
        </div>
        <button class="choice-btn" style="border-color:var(--red);font-size:15px;text-align:center;padding:14px;" onclick="startDungeonCombat('${monsterId}', ${index})">💀 挑战BOSS！</button>
        <button class="continue-btn" onclick="window.goHome()">🏠 撤退</button>`;
      // Boss 入场脉冲效果
      setTimeout(() => {
        const app = document.getElementById('app');
        app.style.boxShadow = 'inset 0 0 80px rgba(192,57,43,0.15)';
        setTimeout(() => { app.style.boxShadow = ''; }, 1200);
      }, 100);
      break;
    }
  }
  
  // 叙事碎片注入 + NPC盟友 + 秘密房间
  if(room.cleared && room.type !== 'boss' && room.type !== 'prep' && Math.random() < 0.25) window.injectLoreFragment(regionId);
  if(room.type !== 'boss' && Math.random() < 0.012) { const ally = window.tryNPCAllyEvent(); if(ally) setTimeout(() => window.triggerNPCAlly(ally), 500); }
  if(room.type !== 'boss' && room.type !== 'prep' && window.checkSecretRoom() && !room._secretChecked) { room._secretChecked = true; setTimeout(() => window.renderSecretRoom(index), 300); }
}

// --- 地下城战斗入口 ---
export function startDungeonCombat(monsterId, roomIndex) {
  const monBase = getMonsterData(monsterId);
  let mon = {...monBase};
  
  // 精英词缀
  if(S._dungeonPendingEliteMod) {
    mon = applyEliteModifier(mon, S._dungeonPendingEliteMod);
  }
  
  // 地下城修饰符
  mon = applyDungeonModifierToMonster(mon);
  
  // 恶魔难度
  if(S._hellMode) {
    mon.hp = Math.floor(mon.hp * 1.3);
    mon.atk = Math.floor(mon.atk * 1.3);
  }
  
  S.combat = {
    monsterId,
    monHp: mon.hp, monMaxHp: mon.hp,
    monAtk: mon.atk, monDef: mon.def||0,
    monName: mon.name, monColor: mon.color||'#c0392b',
    monGold: mon.gold||30, monExp: mon.exp||25,
    monLv: mon.lv||S.lv,
    monDrop: null,
    monStatusAtk: mon.statusAtk||null,
    isBoss: mon.isBoss||false,
    isElite: !!S._dungeonPendingEliteMod,
    multiPhase: mon.multiPhase||false,
    phase2Hp: mon.phase2Hp||0,
    phase2AtkBonus: mon.phase2AtkBonus||0,
    phase2Triggered: false,
    turn: 0,
    log: [],
    parentScene: S.scene,
    dungeonRoomIdx: roomIndex,
    stunTurns: 0,
    weakened: false,
    _eliteModifier: S._dungeonPendingEliteMod || null,
  };
  
  S._dungeonPendingEliteMod = null;
  S._dungeonPendingMonster = null;
  sfxMonsterRoar();
  window.renderCombat();
}

// --- 前进到下一个房间 ---
export function advanceDungeonRoom(currentIndex, skipOne) {
  S._dungeonRooms[currentIndex].cleared = true;
  const nextIdx = skipOne ? currentIndex + 2 : currentIndex + 1;
  
  // 房间间随机事件
  if(currentIndex > 0 || !skipOne) {
    const event = triggerTravelEvent();
    const hadEvent = processTravelEvent(event);
    if(hadEvent && event.effect !== 'combat') {
      // 非战斗事件：显示后让玩家点继续
      const main = document.getElementById('main');
      const continueBtn = main.querySelector('button');
      if(continueBtn && continueBtn.textContent.includes('继续')) {
        // Already has a continue button, modify it
        const oldClick = continueBtn.onclick;
        continueBtn.onclick = function() {
          // Remove travel event display
          const travelEl = main.querySelector('.travel-event');
          if(travelEl) travelEl.remove();
          goToDungeonRoom(nextIdx);
        };
        return;
      }
    }
    if(hadEvent && event.effect === 'combat') {
      // 战斗事件：等战斗结束后继续
      S._nextDungeonRoomAfterCombat = nextIdx;
      return;
    }
  }
  
  goToDungeonRoom(nextIdx);
}

// --- 地下城完成 ---
export function completeCurrentDungeon() {
  if(S.currentDungeon) {
    completeDungeon(S.currentDungeon.id, S.currentDungeonDiff);
  }
  S._dungeonRooms = null;
  S._dungeonRoomIndex = 0;
  S._dungeonRoomCleared = 0;
  S._dungeonModifier = null;
  S._showModifierBadge = false;
  S._hellMode = false;
  
  const regionId = window.getCurrentRegionId() || 'forest';
  S.scene = regionId;
  S.currentDungeon = null;
  S.currentDungeonDiff = null;
  S.hp = Math.min(S.maxHp, S.hp + 20);
  save();
  toast('🏆 地下城探索完成！', 2000);
  window.renderRegionHub(regionId);
}

// --- 地下城中流浪商人 ---
export function spawnDungeonMerchant() {
  const main = document.getElementById('main');
  const items = [];
  const picked = new Set();
  const epics = EQUIPMENT_DB.filter(e => e.tier==='epic' && e.lv <= (S.lv||1) + 5);
  const candidates = epics.length >= 2 ? epics : EQUIPMENT_DB.filter(e => ['rare','unique','epic'].includes(e.tier) && e.lv <= (S.lv||1)+5);
  
  const count = 1 + Math.floor(Math.random() * Math.min(3, candidates.length));
  for(let i = 0; i < count; i++) {
    let item;
    let tries = 0;
    do { item = candidates[Math.floor(Math.random() * candidates.length)]; tries++; } while(picked.has(item.id) && tries < 20);
    if(!picked.has(item.id)) {
      picked.add(item.id);
      items.push({ ...item, price: item.tier==='epic' ? 2000+Math.floor(Math.random()*5000) : 500+Math.floor(Math.random()*1500) });
    }
  }
  
  let html = `<div class="story-box" style="border-color:var(--gold);">
    <strong style="color:var(--gold);">🧳 地下商人出现了！</strong>
    <div style="color:var(--dim);font-size:11px;">「在这种地方也能遇到客人……缘分啊！看看我的货吧。」</div><div class="choices" style="margin-top:8px;">`;
  
  items.forEach((it, i) => {
    const ti = TIERS[it.tier];
    html += `<div class="shop-item" style="border:1px solid ${ti.color};margin-bottom:6px;padding:8px;border-radius:6px;cursor:pointer;" onclick="buyDungeonItem('${it.id}',${it.price})">
      <div style="color:${ti.color};font-weight:bold;">[${ti.name}] ${it.name}</div>
      <div style="font-size:10px;color:var(--dim);">${it.desc?.split('。')[0]||''}</div>
      <div style="color:var(--gold);">💰 ${it.price}G</div>
    </div>`;
    S._dungeonMerchantItems = S._dungeonMerchantItems || {};
    S._dungeonMerchantItems[it.id] = it;
  });
  
  html += `</div><button class="continue-btn" style="margin-top:8px;font-size:11px;" onclick="dismissDungeonMerchant()">不必了，告辞</button></div>`;
  
  const existing = main.querySelector('.story-box');
  if(existing) existing.insertAdjacentHTML('afterend', html);
  else main.insertAdjacentHTML('beforeend', html);
}

export function buyDungeonItem(itemId, price) {
  if(S.gold < price) { toast('金币不足！'); return; }
  if(S.inventory.length >= 128) { toast('背包已满！'); return; }
  S.gold -= price;
  const item = S._dungeonMerchantItems?.[itemId];
  if(!item) return;
  S.ownedEquipIds = S.ownedEquipIds || [];
  S.ownedEquipIds.push(item.id);
  S.inventory.push({ id: item.id, type:'equip' });
  showDropNotify(item);
  toast(`✨ 购得装备：${item.name}！`, 2500);
  S._dungeonMerchantItems = {};
  save();
  dismissDungeonMerchant();
}

export function dismissDungeonMerchant() {
  S._dungeonMerchantItems = {};
  // 移除商人的UI元素
  const main = document.getElementById('main');
  const merchantBox = main.querySelector('.story-box[style*="border-color:var(--gold)"]');
  if(merchantBox) merchantBox.remove();
  
  // 前进到下一房间
  const roomIdx = S._dungeonRoomIndex;
  advanceDungeonRoom(roomIdx);
}

// --- 地下城房间号转换为场景ID（兼容旧系统） ---
export function getDungeonSceneId() {
  if(!S._dungeonRooms) return S.scene;
  return `dungeon_room_${S._dungeonRoomIndex}`;
}

export function findDungeonById(dungeonId) {
  for(const region in DUNGEONS) {
    const found = DUNGEONS[region].find(d => d.id === dungeonId);
    if(found) return found;
  }
  return null;
}

// --- 完成副本记录 ---
export function completeDungeon(dungeonId, difficulty) {
  const diffKey = difficulty || 'normal';
  const flagKey = `dungeon_${dungeonId}_${diffKey}`;
  if(S.flags[flagKey]) return; // 已标记，跳过
  S.flags[flagKey] = true;
  console.log('[副本通关]', dungeonId, diffKey, new Date().toLocaleTimeString());
  toast('🏆 副本通关！' + dungeonId, 2000);
  // 同步区域完成标记
  if(dungeonId === 'gf_dark_thunder') S.flags.beat_forest = true;
  if(dungeonId === 'ht_magic') S.flags.beat_hetonmar = true;
  if(dungeonId === 'dc_citadel') S.flags.beat_dark_city = true;
  if(dungeonId === 'sky_dragon_lair') S.flags.beat_sky = true;
  if(window.trackQuest) { window.trackQuest("clear_dungeon", dungeonId); window.trackQuest("clear_dungeon","any"); }
  // 暗黑城通关奖励：永久+3全属性
  if(dungeonId === 'dc_citadel' && !S._dc_bonus) {
    S._dc_bonus = true;
    S.atk = (S.atk||0) + 3;
    S.def = (S.def||0) + 3;
    S.maxHp = (S.maxHp||100) + 30;
    S.hp = Math.min(S.maxHp, S.hp + 30);
    setTimeout(() => toast('🕯️ 暗黑城通关！揭示了瘟疫真相，全属性永久+3，HP+30', 3000), 500);
  }
  // 立即强制保存，防止标志丢失
  save();
}

// === Boss 胜利面板 ===
export function renderBossVictory(c, gold, exp, dropMsg, progressHTML, isDungeon) {
  const main = document.getElementById('main');
  const monsterId = c.monsterId || '';
  const monImg = window.getBossDefeatedImage(monsterId);
  const diff = S.currentDungeonDiff || 'normal';
  const diffLabel = diff === 'hell' ? '💀 恶魔难度' : '✨ 普通难度';
  
  // 获取下一关信息
  let nextDungHTML = '';
  if(S.currentDungeon) {
    const nextId = getNextDungeon(S.currentDungeon.id);
    if(nextId) {
      const nd = findDungeonById(nextId);
      if(nd) {
        const lvOK = S.lv >= (nd.lvReq||1);
        nextDungHTML = `<button class="choice-btn boss-next-dung" ${lvOK?'':'disabled'} onclick="goNextDungeon('${nextId}')">
          ⚔️ 进入下一关：${nd.name}（${diffLabel}）${lvOK?'':'[需 Lv.'+nd.lvReq+']'}
        </button>`;
      }
    }
  }

  // 战斗统计 — 文本+图标形式
  const statsHTML = `
    <div class="boss-rewards">
      <div class="boss-reward-item"><span class="ri-icon">💰</span> <span class="ri-val">+${gold}</span> 金币</div>
      <div class="boss-reward-item ri-exp"><span class="ri-icon">✨</span> <span class="ri-val">+${exp}</span> 经验</div>
      <div class="boss-reward-item ri-turn"><span class="ri-icon">⚔️</span> <span class="ri-val">${c.turn||0}</span> 回合</div>
      <div class="boss-reward-item ri-hp"><span class="ri-icon">❤️</span> <span class="ri-val">${S.hp}/${S.maxHp}</span> HP</div>
    </div>`;

  // 返回按钮
  let returnAction = isDungeon ? "completeCurrentDungeon()" : "window.goHome()";
  let returnLabel = isDungeon ? "🏠 返回据点" : "🏠 返回大厅";

  // 毛笔X的 SVG
  const crossSVG = `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
    <line x1="15" y1="15" x2="85" y2="85" stroke="rgba(220,40,30,0.7)" stroke-width="8" stroke-linecap="round" stroke-dasharray="120" stroke-dashoffset="120" style="animation:crossStroke 0.5s 0.3s ease-out forwards;"/>
    <line x1="85" y1="15" x2="15" y2="85" stroke="rgba(220,40,30,0.7)" stroke-width="8" stroke-linecap="round" stroke-dasharray="120" stroke-dashoffset="120" style="animation:crossStroke 0.5s 0.5s ease-out forwards;"/>
  </svg>`;

  main.innerHTML = `
    ${progressHTML||''}
    <div class="boss-victory">
      <div class="boss-victory-illust">
        <div class="boss-victory-defeated">DEFEATED</div>
        <div class="boss-cross">${crossSVG}</div>
        ${monImg}
        <div style="position:relative;z-index:2;">
          <div class="boss-victory-title" style="font-size:20px;margin-bottom:2px;">👑  ${c.monName} 击破！</div>
          <div style="color:var(--dim);font-size:10px;">${diffLabel}</div>
        </div>
      </div>
    </div>
    <div class="story-box" style="border-color:rgba(201,164,75,0.2);">
      <div style="text-align:center;color:var(--green);font-size:15px;font-weight:bold;margin-bottom:6px;">🏆 副本通关！</div>
      ${dropMsg ? `<div style="text-align:center;margin-bottom:4px;">${dropMsg}</div>` : ''}
      ${statsHTML}
    </div>
    <div class="boss-victory-actions">
      ${nextDungHTML}
      ${S.currentDungeon ? `<button class="choice-btn boss-retry" onclick="reEnterDungeon()">
        🔄 再次挑战：${S.currentDungeon.name}（${diffLabel}）
      </button>` : ''}
      <button class="continue-btn boss-return" onclick="${returnAction}">${returnLabel}</button>
    </div>`;

  // Boss击杀后30%概率刷流浪商人（保留现有机制）
  if(Math.random() < 0.3) {
    setTimeout(() => spawnWanderingMerchant(), 2000);
  }
}

// --- 下一关 ---
export function getNextDungeon(currentId) {
  for(const chain of Object.values(DUNGEON_CHAINS)) {
    const idx = chain.indexOf(currentId);
    if(idx >= 0 && idx < chain.length - 1) return chain[idx + 1];
  }
  return null;
}

export function goNextDungeon(nextId) {
  const diff = S.currentDungeonDiff || 'normal';
  S.currentDungeon = null;
  S.currentDungeonDiff = null;
  enterDungeon(nextId, diff);
}

export function reEnterDungeon() {
  if(!S.currentDungeon) return;
  const dunId = S.currentDungeon.id;
  const diff = S.currentDungeonDiff || 'normal';
  S.currentDungeon = null;
  S.currentDungeonDiff = null;
  enterDungeon(dunId, diff);
}

// --- 流浪商人 ---
export function spawnWanderingMerchant() {
  const main = document.getElementById('main');
  const epics = EQUIPMENT_DB.filter(e => e.tier==='epic' && e.lv <= (S.lv||1) + 10);
  if(epics.length === 0) return;
  
  // 选 1-3 件史诗
  const count = 1 + Math.floor(Math.random() * 3);
  const items = [];
  const picked = new Set();
  for(let i = 0; i < count && i < epics.length; i++) {
    let item;
    do { item = epics[Math.floor(Math.random() * epics.length)]; } while(picked.has(item.id));
    picked.add(item.id);
    items.push({ ...item, price: 2000 + Math.floor(Math.random() * 8000) });
  }
  
  const avgPrice = Math.floor(items.reduce((s,it)=>s+it.price,0) / items.length);
  const diceCost = Math.max(200, Math.floor(avgPrice / 10));
  
  let html = `<div class="story-box merchant-card">
    <div class="merchant-title">🧳 流浪商人出现了！</div>
    <div style="color:var(--dim);font-size:11px;text-align:center;margin:4px 0 10px;">「嘿嘿，冒险家，看看我的珍藏……敢不敢猜个骰子？」</div>
    <div class="choices">`;
  
  items.forEach((item, i) => {
    const ti = TIERS[item.tier];
    html += `<div class="merchant-item" onclick="window.buyMerchantItem('${item.id}',${item.price},'merchant_${i}')">
      <div style="color:${ti.color};font-weight:bold;">[${ti.name}] ${item.name}</div>
      <div style="font-size:10px;color:var(--dim);">${item.desc?.split('。')[0]||''}</div>
      <div style="color:var(--gold);margin-top:2px;">💰 ${item.price}G</div>
    </div>`;
    S._merchantItems = S._merchantItems || {};
    S._merchantItems['merchant_'+i] = item;
  });
  
  // 骰子游戏
  S._merchantDiceCost = diceCost;
  html += `<div class="merchant-dice">
    <div style="color:var(--pink);font-size:11px;text-align:center;margin-bottom:6px;">🎲 猜点数（1-6），中了全场1金币任选一件！费用：${diceCost}G</div>
    <div style="display:flex;gap:4px;">`;
  for(let n = 1; n <= 6; n++) {
    html += `<button class="choice-btn" style="flex:1;font-size:12px;padding:6px;" onclick="window.playDiceGame(${n})">${n}</button>`;
  }
  html += `</div></div>`;
  
  html += `<button class="continue-btn" style="margin-top:8px;font-size:11px;" onclick="window.dismissMerchant()">不必了，告辞</button></div>`;
  
  main.insertAdjacentHTML('beforeend', html);
}

// --- 非地下城战斗后返回 ---
export function goAfterCombat(scene) {
  S.scene = scene;
  if(scene === 'lobby') {
    window.renderLobby();
  } else {
    window.renderScene();
  }
  renderStatusbar();
}
